import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import WeekPicker from '../ui/WeekPicker';
import { PhoneSearchRow, PhoneSearchToggle, usePhoneSearch } from '../PhoneSearch';
import { BTN } from '../../constants/buttons';
import { SHEET_BOTTOM, SHEET_Z } from '../../constants/overlayLayers';
import { PHONE_FULL_BLEED } from '../../constants/phoneLayout';

/**
 * THE timesheets phone header (below `md`, owner 2026-10-27 — mock "B"):
 * "mobile screen is half full before we get to the actual time sheet".
 * One row: a context CHIP that summarises the view ("Workshop · Team · 10
 * people ▾") beside the compact week arrows. Tapping the chip opens a bottom
 * sheet holding every control that used to be stacked on the page (Team /
 * Mine, Units / All users, Apply to, Search), passed in as `children`.
 * The primary action (Submit week) is NOT here — each screen pins it to the
 * bottom bar (`ui/PhoneActionBar`), in the thumb zone beside Save.
 *
 * No `children` → the chip is plain text (nothing to open).
 * `children` may also be a FUNCTION `({ close }) => nodes` (opt-in, 2026-10-28) — for a
 * control that must close the sheet before it opens a dialog (the sheet sits ABOVE a modal).
 */
export default function PhoneContextBar({ summary, weekStart, onWeekChange, idPrefix, children, search = null, maxWeekStart = null }) {
  const [open, setOpen] = useState(false);
  // 🔍 (optional): THE shared phone search (`PhoneSearch.js`) — a toggle on this row and a search
  // row UNDER it, so the results stay visible. Owner 2026-10-28: search inside the sheet "doesn't
  // work very well as the results are hidden behind the menu".
  const phoneSearch = usePhoneSearch({ searchText: search ? search.text : '', onSearchChange: search ? search.onChange : () => {} });
  const renderChildren = () => (typeof children === 'function' ? children({ close: () => setOpen(false) }) : children);
  const canOpen = typeof children === 'function' || React.Children.toArray(children).some(Boolean);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={`mb-1 border-b border-gray-200 bg-white px-2 py-1.5 dark:border-gray-700 dark:bg-gray-800 ${PHONE_FULL_BLEED}`}>
    <div data-testid={`${idPrefix}-phone-bar`} className="flex items-center gap-1.5">
      {canOpen ? (
        <button type="button" data-testid={`${idPrefix}-phone-chip`} aria-haspopup="dialog" aria-expanded={open}
          onClick={() => setOpen(true)}
          className="flex min-h-[40px] min-w-0 flex-1 items-center gap-1 rounded-full border border-gray-300 px-3 text-left text-sm text-gray-800 dark:border-gray-600 dark:text-gray-100">
          <span className="truncate">{summary}</span>
          <span aria-hidden="true" className="shrink-0 text-xs text-gray-500 dark:text-gray-400">▾</span>
        </button>
      ) : (
        <span className="min-w-0 flex-1 truncate px-1 text-sm font-medium text-gray-800 dark:text-gray-100">{summary}</span>
      )}
      {search && <PhoneSearchToggle idPrefix={idPrefix} search={phoneSearch} label={search.label} />}
      <WeekPicker weekStart={weekStart} onChange={onWeekChange} idPrefix={`${idPrefix}-week`} compact maxWeekStart={maxWeekStart} />
      {open && typeof document !== 'undefined' && createPortal(
        <>
          <div aria-hidden="true" className="fixed inset-0 bg-black/40" style={{ zIndex: SHEET_Z }} onClick={() => setOpen(false)} />
          <div role="dialog" aria-modal="true" aria-label="Timesheet view" data-testid={`${idPrefix}-phone-sheet`}
            className="overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-800"
            style={{ position: 'fixed', left: 8, right: 8, bottom: SHEET_BOTTOM, zIndex: SHEET_Z, maxHeight: '75vh' }}>
            <div className="space-y-4">{renderChildren()}</div>
            <button type="button" data-testid={`${idPrefix}-phone-sheet-done`} className={`${BTN.primary} mt-4 flex min-h-[44px] w-full items-center justify-center`}
              onClick={() => setOpen(false)}>Done</button>
          </div>
        </>,
        document.body
      )}
    </div>
    {search && (
      <PhoneSearchRow idPrefix={idPrefix} search={phoneSearch} searchText={search.text} onSearchChange={search.onChange}
        searchPlaceholder={search.placeholder} searchAriaLabel={search.label} debounceMs={0} searchTestIdPrefix={`${idPrefix}-phone`} />
    )}
    </div>
  );
}

/** A labelled field inside the sheet. */
export function SheetField({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
      {children}
    </div>
  );
}
